import { useState, useEffect } from 'react'
import api from '../../services/api'
import { Upload, Sparkles, TrendingUp, Award, BookOpen, X, FileText, Image as ImageIcon, CheckCircle, AlertCircle, Lightbulb, Target, ClipboardList, Info } from 'lucide-react'
import toast from 'react-hot-toast'
import LoadingSpinner from '../../components/LoadingSpinner'

export default function AITutorFeedback() {
  const [feedback, setFeedback] = useState([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [stats, setStats] = useState(null)
  const [selectedFile, setSelectedFile] = useState(null)
  const [uploadForm, setUploadForm] = useState({
    courseId: '',
    lessonId: '',
    assignmentType: 'graphic_design',
    description: ''
  })
  const [courses, setCourses] = useState([])
  const [lessons, setLessons] = useState([])
  const [loadingLessons, setLoadingLessons] = useState(false)
  const [previewUrl, setPreviewUrl] = useState(null)

  useEffect(() => {
    fetchFeedback()
    fetchStats()
    fetchCourses()
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl)
    }
  }, [])

  const fetchFeedback = async () => {
    try {
      const res = await api.get('/ai-audit/feedback')
      setFeedback(res.data.feedback || [])
    } catch (error) {
      console.error('Failed to fetch feedback:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchStats = async () => {
    try {
      const res = await api.get('/ai-audit/stats')
      setStats(res.data.stats)
    } catch (error) {
      console.error('Failed to fetch stats:', error)
    }
  }

  const fetchCourses = async () => {
    try {
      const res = await api.get('/students/dashboard')
      setCourses(Array.isArray(res.data?.enrollments) ? res.data.enrollments : [])
    } catch (error) {
      console.error('Failed to fetch courses:', error)
    }
  }

  const handleCourseChange = async (courseId) => {
    setUploadForm((prev) => ({ ...prev, courseId, lessonId: '' }))
    setLessons([])
    if (!courseId) return

    try {
      setLoadingLessons(true)
      const res = await api.get(`/students/courses/${courseId}/lessons`)
      setLessons(Array.isArray(res.data?.lessons) ? res.data.lessons : [])
    } catch (err) {
      console.warn('Could not load course lessons for feedback grounding:', err.message)
    } finally {
      setLoadingLessons(false)
    }
  }

  const handleFileSelect = (e) => {
    const file = e.target.files[0]
    if (file) {
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB')
        return
      }
      if (previewUrl) URL.revokeObjectURL(previewUrl)
      setSelectedFile(file)
      if (file.type.startsWith('image/')) {
        setPreviewUrl(URL.createObjectURL(file))
      } else {
        setPreviewUrl(null)
      }
    }
  }

  const handleClearFile = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl)
    setSelectedFile(null)
    setPreviewUrl(null)
  }

  const handleCloseModal = () => {
    setShowUploadModal(false)
    handleClearFile()
  }

  const handleUpload = async (e) => {
    e.preventDefault()
    
    if (!selectedFile && !uploadForm.description.trim()) {
      toast.error('Please select an image file or provide a text description')
      return
    }

    if (!uploadForm.courseId) {
      toast.error('Please select a course')
      return
    }

    setUploading(true)
    const formData = new FormData()
    if (selectedFile) formData.append('assignment', selectedFile)
    formData.append('courseId', uploadForm.courseId)
    if (uploadForm.lessonId) formData.append('lessonId', uploadForm.lessonId)
    formData.append('assignmentType', uploadForm.assignmentType)
    formData.append('description', uploadForm.description)

    try {
      const res = await api.post('/ai-audit/assignment', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      toast.success('Work evaluated! AI formative feedback ready.')
      handleCloseModal()
      setUploadForm({ courseId: '', lessonId: '', assignmentType: 'graphic_design', description: '' })
      fetchFeedback()
      fetchStats()
    } catch (error) {
      const errCode = error.response?.data?.code || error.response?.data?.error_code
      const status = error.response?.status

      if (status === 422 || errCode === 'CONTENT_POLICY_REJECTED') {
        toast.error('Your submission could not be processed because it did not align with our creative content guidelines. Please submit appropriate course-related work.', { duration: 6000 })
        handleClearFile()
      } else if (status === 502 || errCode === 'MODERATION_CHECK_FAILED') {
        toast.error('Image content moderation is temporarily unavailable. Please try again shortly.', { duration: 5000 })
        handleClearFile()
      } else {
        toast.error(error.response?.data?.error || 'Feedback request failed')
      }
    } finally {
      setUploading(false)
    }
  }

  const getScoreColor = (score) => {
    if (score >= 8) return 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20'
    if (score >= 5) return 'text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20'
    return 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20'
  }

  const getScoreText = (score) => {
    if (score >= 8) return 'Excellent'
    if (score >= 5) return 'Good'
    return 'Needs Improvement'
  }

  const scoreColor = (score) => {
    if (score >= 8) return { cls: 'bg-green-500/10 text-green-400 border-green-500/25', label: 'Excellent' }
    if (score >= 5) return { cls: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/25', label: 'Good' }
    return { cls: 'bg-red-500/10 text-red-400 border-red-500/25', label: 'Needs Work' }
  }

  if (loading) return <LoadingSpinner label="Loading feedback" />

  return (
    <div className="space-y-10">
      {/* Header */}
      <div className="cinema-page-header flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <span className="cinema-label block mb-1">Student Portal</span>
          <h1 className="font-display text-2xl text-white font-light flex items-center gap-2">
            <Sparkles size={20} className="text-cinema-glow" /> AI Tutor Feedback
          </h1>
          <p className="font-sans text-sm text-cinema-muted mt-1">Get instant AI-powered feedback on your assignments</p>
        </div>
        <button onClick={() => setShowUploadModal(true)} className="cinema-btn-primary text-xs sm:text-sm px-5 py-2.5 min-h-0 shrink-0">
          <Upload size={15} /> Submit Assignment
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-8 gap-y-8">
          {[
            { label: 'Total Submissions', value: stats.total_submissions || 0 },
            { label: 'Average Score', value: `${stats.average_score ? parseFloat(stats.average_score).toFixed(1) : '0.0'}/10` },
            { label: 'Highest Score', value: `${stats.highest_score || 0}/10` },
          ].map(({ label, value }) => (
            <div key={label} className="cinema-stat">
              <span className="cinema-stat-label">{label}</span>
              <span className="cinema-stat-value">{value}</span>
            </div>
          ))}
        </div>
      )}

      {/* Feedback list */}
      <div className="pt-8 border-t border-white/5">
        {feedback.length === 0 ? (
          <div className="cinema-empty">
            <BookOpen size={36} className="mx-auto mb-4 text-cinema-muted/20" />
            <h3 className="font-display text-lg text-white font-light mb-2">No feedback yet</h3>
            <p className="font-sans text-sm text-cinema-muted mb-5">Submit your first assignment to get AI-powered feedback!</p>
            <button onClick={() => setShowUploadModal(true)} className="cinema-btn-primary text-xs sm:text-sm px-6 py-2.5 min-h-0">
              <Upload size={14} /> Submit Assignment
            </button>
          </div>
        ) : (
          <div className="cinema-list">
            {feedback.map((item) => {
              const sc = scoreColor(item.overall_score)
              const areas = item.areas_for_improvement || []
              const whyMatters = item.why_it_matters || []
              const actions = item.improvement_actions || []
              const hasNewFields = item.overall_feedback || areas.length > 0 || whyMatters.length > 0 || actions.length > 0 || item.next_practice
              return (
                <div key={item.id} className="cinema-list-row flex-col !items-stretch py-5">
                  {/* Header: Course context + formative disclaimer */}
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="inline-flex items-center gap-1.5 text-xs font-sans text-cinema-gold/90 bg-cinema-gold/10 px-2.5 py-1 rounded-sm border border-cinema-gold/25 font-medium">
                          <Info size={13} /> AI Formative Review · Not Official Course Grading
                        </span>
                      </div>
                      <h3 className="font-display text-base sm:text-lg text-white font-normal">{item.course_title || 'Assignment'}</h3>
                      <p className="font-sans text-sm text-cinema-muted mt-0.5 font-medium">{item.file_name}</p>
                      <p className="font-sans text-xs text-cinema-muted/70 mt-1">
                        {new Date(item.created_at).toLocaleDateString()} · {new Date(item.created_at).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-5">
                    {/* Overall Feedback — trainer-style synthesis (T13.2) */}
                    {item.overall_feedback && (
                      <div className="bg-white/[0.02] border border-white/5 rounded-sm p-4 sm:p-5">
                        <p className="font-sans text-sm sm:text-base text-cinema-soft leading-relaxed">{item.overall_feedback}</p>
                      </div>
                    )}

                    {/* What You Did Well */}
                    {item.strengths?.length > 0 && (
                      <div>
                        <p className="flex items-center gap-2 text-xs sm:text-sm uppercase tracking-wider font-semibold mb-2.5 text-green-400">
                          <CheckCircle size={15} /> What You Did Well
                        </p>
                        <ul className="space-y-2">
                          {item.strengths.map((s, i) => (
                            <li key={i} className="font-sans text-sm sm:text-[15px] text-cinema-soft flex items-start gap-2.5 leading-relaxed">
                              <span className="w-1.5 h-1.5 rounded-full bg-green-400 mt-2 shrink-0" />
                              <span>{s}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* What Needs Improvement (T13.2) */}
                    {areas.length > 0 && (
                      <div>
                        <p className="flex items-center gap-2 text-xs sm:text-sm uppercase tracking-wider font-semibold mb-2.5 text-orange-400">
                          <AlertCircle size={15} /> What Needs Improvement
                        </p>
                        <ol className="space-y-2">
                          {areas.map((a, i) => (
                            <li key={i} className="font-sans text-sm sm:text-[15px] text-cinema-soft flex items-start gap-2.5 leading-relaxed">
                              <span className="font-mono text-xs sm:text-sm font-semibold text-cinema-muted shrink-0 mt-0.5">{i + 1}.</span>
                              <span>{a}</span>
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}

                    {/* Fallback: old improvements field for T13.1 records */}
                    {!hasNewFields && item.improvements?.length > 0 && (
                      <div>
                        <p className="flex items-center gap-2 text-xs sm:text-sm uppercase tracking-wider font-semibold mb-2.5 text-primary-400">
                          <TrendingUp size={15} /> Tips for Improvement
                        </p>
                        <ol className="space-y-2">
                          {item.improvements.map((imp, i) => (
                            <li key={i} className="font-sans text-sm sm:text-[15px] text-cinema-soft flex items-start gap-2.5 leading-relaxed">
                              <span className="font-mono text-xs sm:text-sm font-semibold text-cinema-muted shrink-0 mt-0.5">{i + 1}.</span>
                              <span>{imp}</span>
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}

                    {/* Why It Matters (T13.2) */}
                    {whyMatters.length > 0 && (
                      <div>
                        <p className="flex items-center gap-2 text-xs sm:text-sm uppercase tracking-wider font-semibold mb-2.5 text-yellow-400">
                          <Lightbulb size={15} /> Why It Matters
                        </p>
                        <ul className="space-y-2">
                          {whyMatters.map((w, i) => (
                            <li key={i} className="font-sans text-sm sm:text-[15px] text-cinema-soft flex items-start gap-2.5 leading-relaxed">
                              <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 mt-2 shrink-0" />
                              <span>{w}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* How to Improve (T13.2) */}
                    {actions.length > 0 && (
                      <div>
                        <p className="flex items-center gap-2 text-xs sm:text-sm uppercase tracking-wider font-semibold mb-2.5 text-primary-400">
                          <Target size={15} /> How to Improve
                        </p>
                        <ol className="space-y-2">
                          {actions.map((a, i) => (
                            <li key={i} className="font-sans text-sm sm:text-[15px] text-cinema-soft flex items-start gap-2.5 leading-relaxed">
                              <span className="font-mono text-xs sm:text-sm font-semibold text-cinema-muted shrink-0 mt-0.5">{i + 1}.</span>
                              <span>{a}</span>
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}

                    {/* Next Practice (T13.2) */}
                    {item.next_practice && (
                      <div className="pl-3.5 border-l-2 border-cinema-glow/40">
                        <p className="flex items-center gap-2 text-xs sm:text-sm uppercase tracking-wider font-semibold mb-2 text-cinema-glow">
                          <ClipboardList size={15} /> Next Practice
                        </p>
                        <p className="font-sans text-sm sm:text-[15px] text-cinema-soft leading-relaxed">{item.next_practice}</p>
                      </div>
                    )}

                    {/* Fallback: old next_steps for T13.1 records */}
                    {!item.next_practice && item.next_steps && (
                      <div className="pl-3.5 border-l-2 border-primary-500/40">
                        <p className="text-xs sm:text-sm uppercase tracking-wider font-semibold text-primary-400 mb-2">Next Steps</p>
                        <p className="font-sans text-sm sm:text-[15px] text-cinema-soft leading-relaxed">{item.next_steps}</p>
                      </div>
                    )}

                    {/* Encouragement */}
                    {item.encouragement && (
                      <div className="pl-3.5 border-l-2 border-yellow-500/50">
                        <p className="font-sans text-sm sm:text-base text-cinema-soft/90 italic leading-relaxed">"{item.encouragement}"</p>
                      </div>
                    )}

                    {/* Score — demoted to supporting info */}
                    <div className="flex items-center justify-between pt-3.5 border-t border-white/5">
                      <span className="font-sans text-xs text-cinema-muted/80">Formative result — not official grading</span>
                      <span className={`inline-flex items-center gap-2 px-3.5 py-1.5 text-xs sm:text-sm font-sans font-medium border rounded-sm shrink-0 ${sc.cls}`}>
                        {item.overall_score}/10 — {sc.label}
                      </span>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="cinema-glass-strong border border-white/10 rounded-sm w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-5 border-b border-white/8">
              <div>
                <h3 className="font-display text-lg text-white font-light">Submit for AI Review</h3>
                <p className="font-sans text-xs text-cinema-gold/90 mt-1">
                  Formative practice & guidance · Not official grading
                </p>
              </div>
              <button onClick={() => setShowUploadModal(false)} className="text-cinema-muted hover:text-white transition-colors">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleUpload} className="p-6 space-y-5">
              <div>
                <label className="cinema-label block mb-2">Course *</label>
                <select
                  value={uploadForm.courseId}
                  onChange={(e) => handleCourseChange(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 text-white text-sm font-sans px-4 py-2.5 focus:outline-none focus:border-cinema-glow/40 transition-colors"
                  required
                >
                  <option value="" className="bg-gray-900">Select a course</option>
                  {courses.map((course) => (
                    <option key={course.id} value={course.course_id} className="bg-gray-900">
                      {course.title} ({course.course_code})
                    </option>
                  ))}
                </select>
              </div>

              {uploadForm.courseId && (
                <div>
                  <label className="cinema-label block mb-2">
                    Lesson Focus (Recommended for curriculum grounding)
                  </label>
                  <select
                    value={uploadForm.lessonId}
                    onChange={(e) => setUploadForm({ ...uploadForm, lessonId: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 text-white text-sm font-sans px-4 py-2.5 focus:outline-none focus:border-cinema-glow/40 transition-colors"
                    disabled={loadingLessons}
                  >
                    <option value="" className="bg-gray-900">
                      {loadingLessons ? 'Loading lessons…' : 'General Course Review (Auto-resolves current lesson)'}
                    </option>
                    {lessons.map((lesson) => (
                      <option key={lesson.id} value={lesson.id} className="bg-gray-900">
                        {lesson.module_title ? `${lesson.module_title} · ` : ''}{lesson.title}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="cinema-label block mb-2">Assignment Type *</label>
                <select
                  value={uploadForm.assignmentType}
                  onChange={(e) => setUploadForm({...uploadForm, assignmentType: e.target.value})}
                  className="w-full bg-white/5 border border-white/10 text-white text-sm font-sans px-4 py-2.5 focus:outline-none focus:border-cinema-glow/40 transition-colors"
                >
                  {[['graphic_design','Graphic Design'],['photography','Photography'],['video_editing','Video Editing'],['animation','Animation'],['social_media','Social Media Content']].map(([v,l]) => (
                    <option key={v} value={v} className="bg-gray-900">{l}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="cinema-label block mb-2">Work Description / Creative Notes</label>
                <textarea
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm({...uploadForm, description: e.target.value})}
                  className="w-full bg-white/5 border border-white/10 text-white text-sm font-sans px-4 py-2.5 focus:outline-none focus:border-cinema-glow/40 transition-colors resize-none placeholder:text-cinema-muted/40"
                  rows={3}
                  placeholder="Describe your creative intent, process, or specific challenges (optional when submitting an image). The AI evaluates your actual image alongside this description."
                />
              </div>

              <div>
                <label className="cinema-label block mb-2">Upload Creative Work (JPEG, PNG, WebP — Max 10MB)</label>
                {selectedFile ? (
                  <div className="border border-white/15 bg-white/5 p-4 rounded-none">
                    <div className="flex items-center gap-4">
                      {previewUrl ? (
                        <div className="relative w-16 h-16 rounded overflow-hidden border border-white/20 flex-shrink-0 bg-black/40">
                          <img src={previewUrl} alt="Preview" className="w-full h-full object-cover" />
                        </div>
                      ) : (
                        <div className="w-16 h-16 rounded border border-white/20 flex items-center justify-center flex-shrink-0 bg-white/5 text-cinema-glow">
                          <FileText size={24} />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-sans text-sm text-white font-medium truncate">{selectedFile.name}</p>
                        <p className="font-mono text-xs text-cinema-muted">{(selectedFile.size / 1024).toFixed(1)} KB · {previewUrl ? 'Multimodal Image Review Ready' : 'Document'}</p>
                      </div>
                      <button
                        type="button"
                        onClick={handleClearFile}
                        className="p-1.5 hover:bg-white/10 text-cinema-muted hover:text-white transition-colors"
                        title="Remove file"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="border border-dashed border-white/15 hover:border-cinema-glow/40 transition-colors p-6 text-center">
                    <input type="file" onChange={handleFileSelect} accept="image/jpeg,image/png,image/webp,image/*,.pdf,.doc,.docx,.txt" className="hidden" id="file-upload" />
                    <label htmlFor="file-upload" className="cursor-pointer block">
                      <Upload size={28} className="text-cinema-muted/30 mx-auto mb-2" />
                      <p className="font-sans text-sm text-cinema-muted">Click to select image or document</p>
                      <p className="font-mono text-xs text-cinema-muted/80 mt-1 font-medium">Direct AI visual inspection supported for JPEG, PNG, WebP</p>
                    </label>
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={handleCloseModal} disabled={uploading}
                  className="cinema-btn-ghost text-xs px-5 py-2.5 min-h-0 flex-1">
                  Cancel
                </button>
                <button type="submit" disabled={uploading}
                  className="cinema-btn-primary text-xs py-2.5 min-h-0 flex-1 flex items-center justify-center gap-2 disabled:opacity-50">
                  {uploading
                    ? <><LoadingSpinner size="sm" inline /> Submitting...</>
                    : <><Sparkles size={13} /> Get AI Feedback</>
                  }
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
